import React, { useState } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { ReportFiltersState } from './components/filters/ReportFilters';
import ReportFilters from './components/filters/ReportFilters';
import GlobalCoverageSummaryCard from './components/cards/GlobalCoverageSummaryCard';
import LocalizationCoverageCard from '@/components/reports/cards/LocalizationCoverageCard';
import useLocalizationQuality from '@/hooks/useLocalizationQuality';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { useTheme } from '@/contexts/ThemeContext';

const LocalizationCoverageReport: React.FC = () => {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();

  // State for report filters
  const [filters, setFilters] = useState<ReportFiltersState>({});

  // Fetch data using our custom hook for overall report state
  const { isLoading, isError, error, refetch } = useLocalizationQuality(filters);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2
          className={`text-lg font-medium ${actualTheme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}
        >
          {i18n.getLocalizationText('title')}
        </h2>
      </div>

      {/* Filters */}
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        availableFilters={['locale', 'channel', 'family']}
      />

      {isLoading ? (
        <div className="mb-6 grid grid-cols-1 gap-6">
          {/* Loading skeleton placeholders will be handled by each card component */}
          <div className="col-span-1">
            <GlobalCoverageSummaryCard filters={filters} />
          </div>
          <div className="col-span-1">
            <LocalizationCoverageCard filters={filters} />
          </div>
        </div>
      ) : isError ? (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="size-4" />
          <AlertTitle>{i18n.errors.localization.failedToLoad}</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <p>{i18n.getLocalizationText('description')}</p>
            {error instanceof Error && <p className="text-xs opacity-80">{error.message}</p>}
            <Button variant="outline" size="sm" className="mt-2 w-fit" onClick={() => refetch()}>
              <RefreshCw className="mr-2 size-4" />
              {i18n.getCommonText('retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="mb-6 grid grid-cols-1 gap-6">
          {/* Card components will handle their own loading/error states */}
          <div className="col-span-1">
            <GlobalCoverageSummaryCard filters={filters} />
          </div>
          <div className="col-span-1">
            <LocalizationCoverageCard filters={filters} />
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalizationCoverageReport;
